/**
 * Finds the top window in the window hierarchy that shares the same origin.
 *
 * If provided a predicate, it returns the first window that matches the predicate. For example, use `{ predicate: w => 'API' in w }` to find a SCORM player.
 *
 * If provided `{ withOpeners: true }`, it checks at each step in the hierarchy if there is a `window.opener` of the `window.top` with the same origin as the latest window.
 *
 * @param predicate
 * @returns
 */
export default function findTopWindow<T extends Window>({
  withOpeners,
  predicate = () => false
}: {
  withOpeners?: boolean;
  predicate?: (win: Window) => boolean;
} = {}): T | undefined {
  let win = window as Window;

  const nextWindow = (w: Window): Window => {
    // same origin opener?
    try {
      if (withOpeners) {
        const o = w.top?.opener || w.opener;
        if (o && o.location.origin === w.location.origin) return o;
      }
    } catch (error) {}

    // same origin parent?
    try {
      const p = w.parent;
      if (p.location.origin === win.location.origin) return p;
    } catch (error) {}

    // return self to loop
    return w;
  };

  let next = nextWindow(win);

  try {
    while (win !== next && !predicate(win)) {
      win = next;
      next = nextWindow(win);
    }
  } catch (error) {
    return;
  }

  return win as T;
}
